import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CTable,
    CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CSpinner,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CForm, CFormSelect,
    CFormInput, CFormCheck, CBadge, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowLeft, cilPlus, cilPencil, cilTrash, cilBell } from '@coreui/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const NotificationsPage = () => {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ visible: false, data: null });
    const [saving, setSaving] = useState(false);

    // Доступные метрики и операторы (можно перевести через i18n)
    const metrics = [
        { value: 'is_streaming_now', label: 'Статус потока камеры (0-выкл, 1-вкл)' },
        { value: 'people_count', label: 'Количество людей' },
        { value: 'chair_people_count', label: 'Люди на стуле' },
        { value: 'phone_people_count', label: 'Люди в телефоне' },
        { value: 'queue_people_count', label: 'Люди в очереди' },
    ];
    const operators = [{ value: '>', label: '>' }, { value: '<', label: '<' }, { value: '==', label: '==' }, { value: '!=', label: '!=' }];

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [rulesRes, camsRes] = await Promise.all([
                axios.get('/api/notification-rules/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/cameras/', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            setRules(rulesRes.data.results || []);
            setCameras(camsRes.data.results || []);
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (rule = null) => {
        if (rule) {
            setModal({ visible: true, data: { ...rule } });
        } else {
            setModal({
                visible: true,
                data: {
                    name: 'Код № 1',
                    camera: cameras.length > 0 ? cameras[0].id : '',
                    metric: 'people_count', operator: '>', threshold: 0,
                    send_telegram: true,
                    send_email: false,
                    tg_chat_id: '', 
                    email_address: '',
                    cooldown_minutes: 60, 
                    is_active: true,
                    message_template: '⚠️ На камере {camera_name} сработало правило! Значение: {value}'
                }
            });
        }
    };

    const handleSave = async () => {
        // Валидация: должен быть выбран хотя бы один канал связи
        if (!modal.data.send_telegram && !modal.data.send_email) {
            toast.error('Выберите хотя бы один канал связи (Telegram или Email)');
            return;
        }

        // Валидация заполненности полей
        if (modal.data.send_telegram && !modal.data.tg_chat_id.trim()) {
            toast.error('Введите Telegram Chat ID');
            return;
        }
        if (modal.data.send_email && !modal.data.email_address.trim()) {
            toast.error('Введите Email адрес');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            if (modal.data.id) {
                await axios.patch(`/api/notification-rules/${modal.data.id}/`, modal.data, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success('Правило обновлено');
            } else {
                await axios.post('/api/notification-rules/', modal.data, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success('Правило создано');
            }
            setModal({ visible: false, data: null });
            fetchData();
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить это правило?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/notification-rules/${id}/`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Удалено');
            fetchData();
        } catch (err) {
            toast.error(t('common.error'));
        }
    };

    return (
        <div className="bg-light min-vh-100 p-4">
            <CContainer fluid>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2><CIcon icon={cilBell} className="me-2" /> Уведомления</h2>
                    <CButton color="secondary" as={Link} to="/dashboard">
                        <CIcon icon={cilArrowLeft} className="me-2" /> {t('common.back')}
                    </CButton>
                </div>

                <CCard>
                    <CCardHeader className="d-flex justify-content-between align-items-center">
                        <span>Мои правила алертов</span>
                        <CButton color="primary" size="sm" onClick={() => handleOpenModal()}>
                            <CIcon icon={cilPlus} className="me-1" /> Добавить правило
                        </CButton>
                    </CCardHeader>
                    <CCardBody>
                        {loading ? <CSpinner /> : (
                            <div className="table-responsive">
                                <CTable hover>
                                    <CTableHead>
                                        <CTableRow>
                                            <CTableHeaderCell>Камера</CTableHeaderCell>
                                            <CTableHeaderCell>Условие</CTableHeaderCell>
                                            <CTableHeaderCell>Каналы связи</CTableHeaderCell>
                                            <CTableHeaderCell>Кулдаун</CTableHeaderCell>
                                            <CTableHeaderCell>Статус</CTableHeaderCell>
                                            <CTableHeaderCell>Последнее срабатывание</CTableHeaderCell>
                                            <CTableHeaderCell>Действия</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {rules.length === 0 ? (
                                            <CTableRow><CTableDataCell colSpan="7" className="text-center text-muted">У вас пока нет правил</CTableDataCell></CTableRow>
                                        ) : rules.map(rule => (
                                            <CTableRow key={rule.id}>
                                                <CTableDataCell><strong>{rule.camera_name}</strong></CTableDataCell>
                                                <CTableDataCell>
                                                    <CBadge color="info">{rule.metric}</CBadge> {rule.operator} <strong>{rule.threshold}</strong>
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    {rule.send_telegram && <CBadge color="success" className="me-1">TG</CBadge>}
                                                    {rule.send_email && <CBadge color="warning">Email</CBadge>}
                                                    {!rule.send_telegram && !rule.send_email && <span className="text-muted">-</span>}
                                                </CTableDataCell>
                                                <CTableDataCell>{rule.cooldown_minutes} мин.</CTableDataCell>
                                                <CTableDataCell>
                                                    <CBadge color={rule.is_active ? 'success' : 'secondary'}>{rule.is_active ? 'Вкл' : 'Выкл'}</CBadge>
                                                </CTableDataCell>
                                                <CTableDataCell>{rule.last_triggered_at ? dayjs(rule.last_triggered_at).format('DD.MM HH:mm') : 'Никогда'}</CTableDataCell>
                                                <CTableDataCell>
                                                    <CButton color="light" size="sm" className="me-2" onClick={() => handleOpenModal(rule)}><CIcon icon={cilPencil} /></CButton>
                                                    <CButton color="danger" variant="outline" size="sm" onClick={() => handleDelete(rule.id)}><CIcon icon={cilTrash} /></CButton>
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            </div>
                        )}
                    </CCardBody>
                </CCard>
            </CContainer>

            {/* Модалка создания/редактирования */}
            <CModal alignment="center" visible={modal.visible} onClose={() => setModal({ visible: false, data: null })}>
                <CModalHeader><CModalTitle>{modal.data?.id ? 'Редактировать правило' : 'Новое правило'}</CModalTitle></CModalHeader>
                <CModalBody>
                    {modal.data && (
                        <CForm>
                            <div className="mb-3">
                                <label className="form-label">Камера</label>
                                <CFormSelect value={modal.data.camera} onChange={e => setModal({ ...modal, data: { ...modal.data, camera: e.target.value } })}>
                                    {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </CFormSelect>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Название правила (для сообщения)</label>
                                <CFormInput
                                    placeholder="Например: Код № 1"
                                    value={modal.data.name}
                                    onChange={e => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })}
                                />
                            </div>

                            <CRow className="mb-3">
                                <CCol xs={5}>
                                    <label className="form-label">Метрика</label>
                                    <CFormSelect value={modal.data.metric} onChange={e => setModal({ ...modal, data: { ...modal.data, metric: e.target.value } })}>
                                        {metrics.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </CFormSelect>
                                </CCol>
                                <CCol xs={3}>
                                    <label className="form-label">Условие</label>
                                    <CFormSelect value={modal.data.operator} onChange={e => setModal({ ...modal, data: { ...modal.data, operator: e.target.value } })}>
                                        {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </CFormSelect>
                                </CCol>
                                <CCol xs={4}>
                                    <label className="form-label">Значение</label>
                                    <CFormInput type="number" value={modal.data.threshold} onChange={e => setModal({ ...modal, data: { ...modal.data, threshold: e.target.value } })} />
                                </CCol>
                            </CRow>
                                
                            {/* БЛОК КАНАЛОВ СВЯЗИ */}
                            <div className="mb-4 p-3 border rounded bg-light">
                                <label className="form-label fw-bold">Каналы уведомлений</label>
                                <div className="d-flex gap-4 mb-3">
                                    <CFormCheck 
                                        label="Telegram" 
                                        checked={modal.data.send_telegram} 
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, send_telegram: e.target.checked } })} 
                                    />
                                    <CFormCheck 
                                        label="Email" 
                                        checked={modal.data.send_email} 
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, send_email: e.target.checked } })} 
                                    />
                                </div>

                                {modal.data.send_telegram && (
                                    <div className="mb-3">
                                        <label className="form-label small">Telegram Chat ID</label>
                                        <CFormInput 
                                            value={modal.data.tg_chat_id} 
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, tg_chat_id: e.target.value } })} 
                                            placeholder="Введите ID чата"
                                        />
                                        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffe69c', fontSize: '0.85rem' }}>
                                            <div className="text-danger fw-bold mb-1">⚠️ Обязательный шаг для Telegram:</div>
                                            Бот не сможет отправлять вам уведомления, пока вы не разрешите ему это сделать.
                                            Перейдите в нашего бота{' '}
                                            <a href="https://t.me/neimarker_alert_bot" target="_blank" rel="noopener noreferrer" className="fw-bold text-decoration-none">
                                                @neimarker_alert_bot
                                            </a>
                                            {' '}и нажмите <strong>«ЗАПУСТИТЬ»</strong>.<br />
                                            <span className="text-muted">
                                                💡 Узнать свой ID можно в боте{' '}
                                                <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                    @userinfobot
                                                </a>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {modal.data.send_email && (
                                    <div className="mb-1">
                                        <label className="form-label small">Email адрес</label>
                                        <CFormInput 
                                            type="email"
                                            value={modal.data.email_address} 
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, email_address: e.target.value } })} 
                                            placeholder="example@mail.ru"
                                        />
                                    </div>
                                )}
                            </div>

                            <CRow className="mb-3">
                                <CCol xs={6}>
                                    <label className="form-label">Кулдаун (минут)</label>
                                    <CFormInput type="number" value={modal.data.cooldown_minutes} onChange={e => setModal({ ...modal, data: { ...modal.data, cooldown_minutes: e.target.value } })} />
                                    <small className="text-muted">Задержка между повторными отправками.</small>
                                </CCol>
                            </CRow>

                            <div className="mb-3">
                                <label className="form-label">Текст сообщения</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={modal.data.message_template}
                                    onChange={e => setModal({ ...modal, data: { ...modal.data, message_template: e.target.value } })}
                                ></textarea>
                                <small className="text-muted">Можно использовать: {'{rule_name}'}, {'{camera_name}'}, {'{value}'}</small>
                            </div>

                            <CFormCheck label="Правило активно" checked={modal.data.is_active} onChange={e => setModal({ ...modal, data: { ...modal.data, is_active: e.target.checked } })} />
                        </CForm>
                    )}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setModal({ visible: false, data: null })}>Отмена</CButton>
                    <CButton color="primary" onClick={handleSave} disabled={saving}>{saving ? <CSpinner size="sm" /> : 'Сохранить'}</CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default NotificationsPage;